import { _ as __rest } from './_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useMemo, useCallback, Children, isValidElement, cloneElement, useEffect } from 'react';
import { announce } from '@react-aria/live-announcer';
import { PopperUnstyled } from '@mui/base';
import { generateClasses, platform } from '@ids/tools';
import { s as styleInject } from './style-inject.es.js';
import { u as useDefaultState } from './use-default-state.js';
import { u as useId } from './use-id.js';

var css_248z = "/*\n** Voxel Foundation Formats\n*/\n/*\n** Voxel Foundation Media Query Mixins\n*/\n.ids-overlay-tooltip {\n  z-index: 0;\n}\n\n.ids-tooltip {\n  -webkit-box-align: center;\n      -ms-flex-align: center;\n          align-items: center;\n  color: var(--ids_color_contrast_onDark);\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  -webkit-box-orient: vertical;\n  -webkit-box-direction: reverse;\n      -ms-flex-direction: column-reverse;\n          flex-direction: column-reverse;\n  -webkit-box-pack: center;\n      -ms-flex-pack: center;\n          justify-content: center;\n  text-align: left;\n}\n.ids-tooltip .ids-icon {\n  color: inherit;\n  margin-right: 0.9375rem;\n}\n.ids-tooltip__text {\n  -webkit-box-align: center;\n      -ms-flex-align: center;\n          align-items: center;\n  background-color: var(--ids_color_feedback_information);\n  border-radius: var(--ids_size_cornerRadius_tooltip);\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  font: var(--ids_textStyle_f02_s14_h20_wrg);\n  line-height: 1.09375rem;\n  max-width: 13.25rem;\n  padding: var(--ids_size_general_12x);\n}\n@media (min-width: 64rem) {\n  .ids-tooltip__text {\n    max-width: 19rem;\n  }\n}\n.ids-tooltip::before {\n  border: var(--ids_size_general_4x) solid transparent;\n  border-top-color: var(--ids_color_feedback_information);\n  content: \"\";\n  margin: 0 var(--ids_size_general_5x);\n  opacity: 0;\n}\n.ids-tooltip--arrow::before {\n  border-width: var(--ids_size_general_8x);\n  opacity: 1;\n}\n.ids-tooltip--description .ids-tooltip__text {\n  background-color: var(--ids_color_feedback_neutral);\n  color: var(--ids_color_contrast_onDark);\n}\n.ids-tooltip--description::before {\n  border-top-color: var(--ids_color_feedback_neutral);\n}\n.ids-tooltip--bottom {\n  -webkit-box-orient: vertical;\n  -webkit-box-direction: normal;\n      -ms-flex-direction: column;\n          flex-direction: column;\n}\n.ids-tooltip--bottom::before {\n  border-color: transparent;\n  border-bottom-color: var(--ids_color_feedback_information);\n}\n.ids-tooltip--left {\n  -webkit-box-orient: horizontal;\n  -webkit-box-direction: reverse;\n      -ms-flex-direction: row-reverse;\n          flex-direction: row-reverse;\n}\n.ids-tooltip--left::before {\n  border-color: transparent;\n  border-left-color: var(--ids_color_feedback_information);\n  margin: var(--ids_size_general_5x) 0;\n}\n.ids-tooltip--right {\n  -webkit-box-orient: horizontal;\n  -webkit-box-direction: normal;\n      -ms-flex-direction: row;\n          flex-direction: row;\n}\n.ids-tooltip--right::before {\n  border-color: transparent;\n  border-right-color: var(--ids_color_feedback_information);\n  margin: var(--ids_size_general_5x) 0;\n}\n.ids-tooltip--right.ids-tooltip--description::before {\n  border-right-color: var(--ids_color_feedback_neutral);\n}\n.ids-tooltip--bottom.ids-tooltip--description::before {\n  border-bottom-color: var(--ids_color_feedback_neutral);\n}\n.ids-tooltip--left.ids-tooltip--description::before {\n  border-left-color: var(--ids_color_feedback_neutral);\n}\n.ids-tooltip--start {\n  -webkit-box-align: start;\n      -ms-flex-align: start;\n          align-items: flex-start;\n  -webkit-box-pack: start;\n      -ms-flex-pack: start;\n          justify-content: flex-start;\n}\n.ids-tooltip--end {\n  -webkit-box-align: end;\n      -ms-flex-align: end;\n          align-items: flex-end;\n  -webkit-box-pack: end;\n      -ms-flex-pack: end;\n          justify-content: flex-end;\n}\n.ids-tooltip--left, .ids-tooltip--right {\n  -webkit-box-pack: initial;\n      -ms-flex-pack: initial;\n          justify-content: initial;\n}";
styleInject(css_248z);

/**
 * @category Component
 *
 * @example
 * ```ts
 * import { IdsTooltip } from '@ids/react';
 * ```
 */
const IdsTooltip = _a => {
  var {
      id: externalId,
      type = 'informative',
      position = 'top',
      align = 'middle',
      className = '',
      content,
      showArrow,
      open: externalOpen,
      scrollStrategy = 'reposition',
      setOpen: setExternalOpen,
      children,
      popperProps
    } = _a,
    props = __rest(_a, ["id", "type", "position", "align", "className", "content", "showArrow", "open", "scrollStrategy", "setOpen", "children", "popperProps"]);
  const id = useId(externalId, 'ids-tooltip');
  const [isReady, setIsReady] = useState(false);
  const [open, setOpen] = useDefaultState(Boolean(externalOpen), setExternalOpen);
  const anchorRef = useRef();
  const tooltipRef = useRef();
  const portalTooltipRef = useRef();
  const wrapperRef = useRef();
  const placement = `${position}${align !== 'middle' ? align === 'end' ? '-end' : '-start' : ''}`;
  const memoClasses = useMemo(() => generateClasses({
    'ids-tooltip': true,
    'ids-tooltip--right': position === 'right',
    'ids-tooltip--bottom': position === 'bottom',
    'ids-tooltip--left': position === 'left',
    'ids-tooltip--start': align === 'start',
    'ids-tooltip--end': align === 'end',
    'ids-tooltip--description': type === 'description',
    [className]: className
  }), [align, className, position, type]);
  const memoArrowClasses = useMemo(() => generateClasses({
    'ids-tooltip__arrow': true,
    'ids-tooltip__arrow--show': showArrow
  }), [showArrow]);
  // usado com useCallback para ter referência para fazer removeListener no unMound do componente
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  // usado com useCallback para ter referência para fazer removeListener no unMound do componente
  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const handleClickDocument = useCallback(event => {
    const target = event.target;
    if (!tooltipRef.current) {
      return;
    }
    /* istanbul ignore next - essa função está testada de forma assíncrona */
    if (tooltipRef.current.contains(target) || portalTooltipRef.current.contains(target) || target.isEqualNode(tooltipRef.current) || target.isEqualNode(portalTooltipRef.current)) {
      return;
    }
    handleClose();
  }, [handleClose, tooltipRef]);
  const handlePressEsc = useCallback(event => {
    if (event.key === 'Escape' || event.code === 'Escape') {
      handleClose();
    }
  }, [handleClose]);
  const memoChildrenWithProps = useMemo(() => Children.map(children, child => {
    if ( /*#__PURE__*/isValidElement(child)) {
      return /*#__PURE__*/cloneElement(child, {
        onClick: type === 'informative' && !open ? handleOpen : undefined,
        onMouseEnter: type === 'description' ? handleOpen : undefined,
        onFocus: type === 'description' ? handleOpen : undefined,
        onMouseLeave: type === 'description' ? handleClose : undefined,
        onBlur: type === 'description' ? handleClose : undefined,
        'aria-describedby': open ? id : undefined,
        'aria-expanded': open,
        ref: anchorRef
      });
    }
    return child;
  }), [children, handleClose, handleOpen, id, open, type]);
  const renderTooltip = useCallback(({
    popperContainer,
    ref
  }) => {
    if (!isReady || !anchorRef.current) {
      return;
    }
    return jsx(PopperUnstyled, Object.assign({}, popperProps, {
      id: popperContainer ? `${id}-reading` : id,
      placement: placement,
      open: Boolean(open),
      anchorEl: anchorRef.current,
      container: popperContainer,
      popperOptions: {
        strategy: 'fixed'
      }
    }, {
      children: jsxs("div", Object.assign({
        role: "tooltip",
        className: memoClasses,
        ref: ref
      }, props, {
        children: [jsx("div", {
          className: memoArrowClasses
        }), jsx("div", Object.assign({
          className: "ids-tooltip__text"
        }, {
          children: content
        }))]
      }))
    }));
  }, [content, id, isReady, memoArrowClasses, memoClasses, open, placement, popperProps, props]);
  useEffect(() => {
    // cria o announce no DOM
    announce('', 'assertive');
    announce('', 'polite');
  }, []);
  useEffect(() => {
    if (anchorRef.current) {
      setIsReady(true);
    }
  }, [anchorRef]);
  // estratégia de fechar ao rolar
  useEffect(() => {
    if (open && scrollStrategy === 'close') {
      document.addEventListener('scroll', handleClose);
      return;
    }
    document.removeEventListener('scroll', handleClose);
  }, [handleClose, open, scrollStrategy, setOpen]);
  // estratégia de bloquear rolagem
  useEffect(() => {
    // melhorar a lógica e mover para tools
    function toggleBodyScroll(block = true) {
      document.documentElement.style.overflow = block ? 'hidden' : 'initial';
    }
    if (open && scrollStrategy === 'block') {
      toggleBodyScroll();
    } else {
      toggleBodyScroll(false);
    }
  }, [open, scrollStrategy]);
  // fecha clicando em qualquer lugar, menos no tooltip
  useEffect(() => {
    if (type === 'description') {
      return;
    }
    if (open) {
      setTimeout(() => {
        document.addEventListener('click', handleClickDocument, false);
      });
      return;
    }
    document.removeEventListener('click', handleClickDocument, false);
  }, [handleClickDocument, type, open, setOpen]);
  // fecha se pressionar esc
  useEffect(() => {
    if (type === 'description') {
      return;
    }
    if (open) {
      setTimeout(() => {
        document.addEventListener('keydown', handlePressEsc);
      });
      return;
    }
    document.removeEventListener('keydown', handlePressEsc);
  }, [handlePressEsc, type, open, setOpen]);
  // anuncia o texto quando abre
  useEffect(() => {
    if (open && (platform.os === 'Android' || platform.os === 'IOS')) {
      announce(content, 'polite', 1000);
    }
  }, [content, open]);
  return jsxs("span", Object.assign({
    ref: wrapperRef
  }, {
    children: [memoChildrenWithProps, renderTooltip({
      popperContainer: wrapperRef.current,
      ref: tooltipRef
    }), renderTooltip({
      ref: portalTooltipRef
    })]
  }));
};

export { IdsTooltip as I };
//# sourceMappingURL=tooltip.js.map
